// Rebuy Recommendations provider (scaffold).
//
// This is wired to Rebuy's documented "custom" data-source endpoint but stays
// inert until an API key + data-source id are supplied via the theme settings.
// Once Rebuy is connected, its rules engine can power all three scenarios; the
// orchestrator only needs cross-sell (scenario 3) from here because bundle
// scenarios 1 & 2 are currently resolved theme-side. To move those into Rebuy,
// have this map upgrade-type responses (with `removeKeys`) too.
//
// Docs: https://developers.rebuyengine.com/reference/recommendations

import { payingItems } from "./bundles";
import type {
  Cart,
  RecommendationProvider,
  UpsellConfig,
  UpsellRecommendation,
} from "./types";

interface RebuyVariant {
  id: number;
  title: string;
  price: string | number;
  compare_at_price: string | number | null;
  available: boolean;
}

interface RebuyProduct {
  id: number;
  title: string;
  image: { src: string } | null;
  variants: RebuyVariant[];
}

/** Rebuy returns prices as decimal strings ("48.00"); normalize to cents. */
function toCents(value: string | number | null | undefined): number {
  if (value == null) return 0;
  return Math.round(Number(value) * 100);
}

function toRecommendation(product: RebuyProduct): UpsellRecommendation | null {
  const variant =
    product.variants?.find((v) => v.available) ?? product.variants?.[0];
  if (!variant) return null;

  const price = toCents(variant.price);
  const compareAt = toCents(variant.compare_at_price);

  return {
    id: variant.id,
    productTitle: product.title,
    variantTitle: variant.title !== "Default Title" ? variant.title : undefined,
    image: product.image?.src ?? "",
    price,
    compareAtPrice: compareAt > price ? compareAt : undefined,
    actionType: "add",
    actionLabel: "Add to Cart",
  };
}

export const rebuyProvider: RecommendationProvider = {
  async getComplementary(
    cart: Cart,
    limit: number,
    config: UpsellConfig,
  ): Promise<UpsellRecommendation[]> {
    const { apiKey, dataSourceId } = config.rebuy;
    // Inert without credentials — keeps the module silent rather than erroring.
    if (!apiKey || !dataSourceId) return [];

    const items = payingItems(cart.items ?? [], config.giftVariantId);
    if (items.length === 0) return [];

    const params = new URLSearchParams({ key: apiKey, limit: String(limit) });
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      params.append(`cart[items][${i}][product_id]`, String(item.product_id));
      params.append(`cart[items][${i}][quantity]`, String(item.quantity));
      params.append(`cart[items][${i}][variant_id]`, String(item.variant_id));
      params.append(`shopify_product_ids`, String(item.product_id));
    }

    const response = await fetch(
      `https://rebuyengine.com/api/v1/custom/id/${dataSourceId}?${params}`,
    );
    if (!response.ok) return [];

    const { data = [] } = (await response.json()) as { data?: RebuyProduct[] };
    const inCart = new Set(items.map((item) => item.product_id));

    return data
      .filter((product) => !inCart.has(product.id))
      .map(toRecommendation)
      .filter((rec): rec is UpsellRecommendation => rec !== null)
      .slice(0, limit);
  },
};
